import {
  BasicButton,
  ButtonGroup,
  Grid,
  GridItem,
  LegacyModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTrigger,
  PrimaryButton,
  Toggle,
  DateSelectField,
  Link,
} from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { getSchema } from "./schema";
import {
  AddressInput,
  initialValues as initialAddressValues,
} from "../../../../components/AddressInput";

type AddressModalProps = {
  index: number;
  address?: components["schemas"]["AddressesCheckFieldValue"];
  allowCurrent: boolean;
  onSave: (
    address: components["schemas"]["AddressesCheckFieldValue"],
    index: number
  ) => void;
  onDelete?: () => void;
  trigger: JSX.Element;
};
export function AddressModal({
  address,
  allowCurrent,
  onSave,
  onDelete,
  index,
  trigger,
}: AddressModalProps) {
  const { t } = useTranslation();
  const [isCurrentAddress, setIsCurrentAddress] = useState<boolean>();

  useEffect(() => {
    setIsCurrentAddress(!!address && !address.end_date);
  }, [index, address]);

  return (
    <ModalTrigger trigger={() => trigger}>
      {(close) => (
        <Formik
          initialValues={
            address ?? {
              ...initialAddressValues,
              start_date: null,
              end_date: null,
              index,
            }
          }
          onSubmit={(address) => {
            onSave(
              address as components["schemas"]["AddressesCheckFieldValue"],
              index
            );
            close();
          }}
          validationSchema={getSchema(Boolean(isCurrentAddress))}
        >
          {({ handleSubmit, setFieldValue }) => {
            return (
              <LegacyModal width="456px">
                <ModalHeader
                  close={close}
                  title={address ? t("Edit address") : t("Add address")}
                />
                <ModalContent>
                  <Form>
                    <Grid form>
                      <AddressInput />

                      <GridItem>
                        <Field
                          label={t("Start date")}
                          name="start_date"
                          isApproximateDate
                          isRequired
                          formatOverride="mm-yy"
                          as={DateSelectField}
                          testID="address-history-start-date-select"
                        />
                      </GridItem>

                      {allowCurrent && (
                        <GridItem
                          alignItems="left"
                          className={css({
                            flexDirection: "row",
                          })}
                        >
                          <Toggle
                            isSelected={isCurrentAddress}
                            onChange={(value) => {
                              setFieldValue("end_date", null);
                              setIsCurrentAddress(value);
                            }}
                            testID="address-history-current-address"
                          >
                            <Trans>I currently live at this address</Trans>
                          </Toggle>
                        </GridItem>
                      )}

                      {!isCurrentAddress && (
                        <GridItem>
                          <Field
                            label={t("End date")}
                            name="end_date"
                            isApproximateDate
                            isRequired
                            formatOverride="mm-yy"
                            as={DateSelectField}
                            testID="address-history-end-date-select"
                          />
                        </GridItem>
                      )}
                    </Grid>
                  </Form>
                </ModalContent>
                <ModalFooter
                  className={css({
                    justifyContent: onDelete ? "space-between" : "flex-end",
                  })}
                >
                  {onDelete && (
                    <Link
                      textStyle="global/body-secondary"
                      textColor="text-weak"
                      onPress={() => {
                        onDelete();
                        close();
                      }}
                      testID="address-history-delete-address-button"
                    >
                      <Trans>Delete address</Trans>
                    </Link>
                  )}
                  <ButtonGroup>
                    <BasicButton
                      size="medium"
                      onPress={close}
                      testID="address-history-cancel-button"
                    >
                      <Trans>Cancel</Trans>
                    </BasicButton>
                    <PrimaryButton
                      testID="address-history-add-address-save-button"
                      type="submit"
                      size="medium"
                      onPress={() => handleSubmit()}
                    >
                      <Trans>Save address</Trans>
                    </PrimaryButton>
                  </ButtonGroup>
                </ModalFooter>
              </LegacyModal>
            );
          }}
        </Formik>
      )}
    </ModalTrigger>
  );
}
